import { create } from "zustand";
import {
  addComment,
  listComments,
  resolveComment,
  togglePinComment,
  type CommentRow,
  type CommentTarget,
} from "../supabase/api";

interface CommentsState {
  projectId: string | null;
  token: string | null;
  comments: CommentRow[];
  loading: boolean;
  error: string | null;

  loadFor(projectId: string | null, token: string | null): Promise<void>;
  add(target: CommentTarget, body: string, authorName: string): Promise<void>;
  resolve(id: string): Promise<void>;
  togglePin(id: string): Promise<void>;
  clear(): void;
}

export const useCommentsStore = create<CommentsState>((set, get) => ({
  projectId: null,
  token: null,
  comments: [],
  loading: false,
  error: null,

  loadFor: async (projectId, token) => {
    if (!projectId && !token) {
      set({ projectId: null, token: null, comments: [], error: null });
      return;
    }
    set({ projectId, token, loading: true, error: null });
    try {
      const rows = await listComments(projectId ?? "", { token });
      set({ comments: rows, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false, comments: [] });
    }
  },

  add: async (target, body, authorName) => {
    const { projectId, token } = get();
    if (!projectId && !token) throw new Error("No active project");
    const row = await addComment(projectId ?? "", target, body, authorName, { token });
    set((s) => ({ comments: [...s.comments, row] }));
  },

  resolve: async (id) => {
    const { token } = get();
    await resolveComment(id, { token });
    set((s) => ({
      comments: s.comments.map((c) =>
        c.id === id ? { ...c, resolved_at: new Date().toISOString() } : c,
      ),
    }));
  },

  togglePin: async (id) => {
    // The RPC clears any prior pin on the same target; mirror it or the
    // optimistic pass flickers.
    const current = get().comments.find((c) => c.id === id);
    if (!current) return;
    const willPin = current.pinned_at === null;
    const now = new Date().toISOString();
    const sameTarget = (c: CommentRow): boolean =>
      c.project_id === current.project_id &&
      ((current.cable_id !== null && c.cable_id === current.cable_id) ||
        (current.node_id !== null && c.node_id === current.node_id));
    set((s) => ({
      comments: s.comments.map((c) => {
        if (c.id === id) {
          return { ...c, pinned_at: willPin ? now : null };
        }
        if (willPin && c.id !== id && sameTarget(c) && c.pinned_at) {
          return { ...c, pinned_at: null };
        }
        return c;
      }),
    }));
    try {
      const finalPinned = await togglePinComment(id);
      set((s) => ({
        comments: s.comments.map((c) =>
          c.id === id
            ? { ...c, pinned_at: finalPinned ? c.pinned_at ?? now : null }
            : c,
        ),
      }));
    } catch (e) {
      set((s) => ({
        comments: s.comments.map((c) =>
          c.id === id ? { ...c, pinned_at: current.pinned_at } : c,
        ),
      }));
      throw e;
    }
  },

  clear: () => set({ projectId: null, token: null, comments: [], error: null }),
}));

/** Active (non-resolved) comments for a target, pinned first then oldest. */
export function commentsForNode(comments: CommentRow[], nodeId: string): CommentRow[] {
  return sortPinnedThenChrono(
    comments.filter((c) => c.node_id === nodeId && !c.resolved_at),
  );
}
export function commentsForCable(comments: CommentRow[], cableId: string): CommentRow[] {
  return sortPinnedThenChrono(
    comments.filter((c) => c.cable_id === cableId && !c.resolved_at),
  );
}

function sortPinnedThenChrono(items: CommentRow[]): CommentRow[] {
  return [...items].sort((a, b) => {
    const ap = a.pinned_at ? 1 : 0;
    const bp = b.pinned_at ? 1 : 0;
    if (ap !== bp) return bp - ap;
    return Date.parse(a.created_at) - Date.parse(b.created_at);
  });
}

/** Resolved-but-not-deleted comments for a target, newest first. */
export function resolvedCommentsForNode(
  comments: CommentRow[],
  nodeId: string,
): CommentRow[] {
  return comments
    .filter((c) => c.node_id === nodeId && c.resolved_at)
    .sort(
      (a, b) =>
        Date.parse(b.resolved_at ?? b.created_at) -
        Date.parse(a.resolved_at ?? a.created_at),
    );
}
export function resolvedCommentsForCable(
  comments: CommentRow[],
  cableId: string,
): CommentRow[] {
  return comments
    .filter((c) => c.cable_id === cableId && c.resolved_at)
    .sort(
      (a, b) =>
        Date.parse(b.resolved_at ?? b.created_at) -
        Date.parse(a.resolved_at ?? a.created_at),
    );
}
